// Two analysers: the mic (always available while a conversation is open, for silence
// detection and barge-in) and Griot's playback (for lip-sync while it speaks).
let ctx = null;
let micAn = null;
let playAn = null;
let micSrc = null;
let playSrc = null;
let micBuf = null;
let playBuf = null;
let freqBuf = null;
let source = "mic";
let gain = null;          // Griot's voice passes through here, so our slider can move it
let lim = null;           // and through here, so lifting a quiet voice cannot clip
let out = false;          // is the Web Audio path actually carrying the sound?
let vol = 1;
let trim = 1;             // per-voice correction: ElevenLabs renders some voices far quieter than others

// The gain carries the slider AND the per-voice trim. Headroom above 1 is deliberate: a quiet voice
// has to be lifted, and the limiter after it keeps the peaks honest.
const applyGain = () => { if (gain) gain.gain.value = Math.max(0, Math.min(2.4, vol * trim)); };

// the only path to the speakers; connecting a pair that is already connected is a no-op
const openOut = () => {
  gain.connect(lim);
  lim.connect(ctx.destination);
  out = true;
};

const AC = () => window.AudioContext || window.webkitAudioContext;

const ensure = () => {
  if (ctx) return true;
  const C = AC();
  if (!C) return false;
  ctx = new C();
  const mk = () => {
    const a = ctx.createAnalyser();
    a.fftSize = 1024;
    a.smoothingTimeConstant = 0.6;
    return a;
  };
  micAn = mk();
  playAn = mk();
  gain = ctx.createGain();
  applyGain();
  // A LIMITER, NOT AN EFFECT. Lifting the quieter voice by 7 dB to match the other would otherwise
  // clip her loudest syllables; this holds the peaks just under full scale instead. Knee 0 and a
  // 20:1 ratio make it a ceiling rather than a compressor, so nothing is squashed below it.
  lim = ctx.createDynamicsCompressor();
  lim.threshold.value = -2;
  lim.knee.value = 0;
  lim.ratio.value = 20;
  lim.attack.value = 0.003;
  lim.release.value = 0.12;
  micBuf = new Uint8Array(micAn.fftSize);
  playBuf = new Uint8Array(playAn.fftSize);
  freqBuf = new Uint8Array(playAn.frequencyBinCount);
  return true;
};

export const resume = () => {
  if (ensure() && ctx.state === "suspended") ctx.resume().catch(() => {});
};

export const attachStream = (stream) => {
  if (!ensure()) return;
  try { micSrc?.disconnect(); } catch { /* gone */ }
  micSrc = ctx.createMediaStreamSource(stream);
  micSrc.connect(micAn);
  resume();
};

export const detachStream = () => {
  try { micSrc?.disconnect(); } catch { /* gone */ }
  micSrc = null;
};

export const attachElement = (el) => {
  if (!ensure()) return;
  try { playSrc?.disconnect(); } catch { /* gone */ }
  playSrc = ctx.createMediaElementSource(el);
  playSrc.connect(playAn);
  playSrc.connect(ctx.destination);
  resume();
};

// Griot's voice when a human voice is reading his words: an <audio> element with a plain src. Routed
// through the analyser (so the presence breathes with him) and through our gain (so the volume
// slider works everywhere).
//
// AN ELEMENT CAN ONLY BE PIPED INTO WEB AUDIO ONCE, AND IT IS A ONE-WAY DOOR. From the moment
// `createMediaElementSource` is called, the element stops making any sound of its own — everything
// it plays goes out through this graph or nowhere at all. So the node is kept for the life of the
// page (`playElSrc`) and a second sitting RE-OPENS the same path instead of walking away: ending a
// sitting disconnects the gain, and before this it was never reconnected, which is how the second
// flowing line on one page came up silent while the words still appeared on screen.
// A suspended context would swallow a brand-new source entirely, so until it is running we leave an
// uncaptured element to play natively and try again on the next sentence.
let playEl = null;
let playElSrc = null;
export const attachPlayback = (el) => {
  if (!ensure() || !el) return false;
  if (playEl === el && playElSrc) {
    try {
      playSrc = playElSrc;
      playSrc.connect(playAn);      // a connection that already exists is a no-op
      playSrc.connect(gain);
      openOut();
    } catch { out = false; }
    resume();
    return out;
  }
  if (ctx.state !== "running") return false;
  try {
    try { playSrc?.disconnect(); } catch { /* gone */ }
    playElSrc = ctx.createMediaElementSource(el);
    playSrc = playElSrc;
    playSrc.connect(playAn);
    playSrc.connect(gain);
    openOut();
    playEl = el;
    return true;
  } catch {
    out = false;
    return false;
  }
};

// Has this element already been piped into the graph? If so it has no voice of its own any more,
// and anything it plays — a live WebRTC track included — has to take the Web Audio path.
export const playbackCaptured = (el) => !!el && playEl === el;

// Griot's voice when it arrives over WebRTC. Routed through our own gain node to the speakers
// rather than left to the <audio> element, for two reasons: an element's volume is read-only on
// iOS, and iOS sends element playback of a live track to the EARPIECE while Web Audio goes to the
// loudspeaker. Returns whether this path is live — the caller mutes the element only if it is.
export const attachRemote = (stream, toSpeaker = true) => {
  if (!ensure()) return false;
  try { playSrc?.disconnect(); } catch { /* gone */ }
  playSrc = ctx.createMediaStreamSource(stream);
  playSrc.connect(playAn);
  out = false;
  if (toSpeaker) {
    try {
      playSrc.connect(gain);
      openOut();
    } catch { out = false; }
  }
  resume();
  return out;
};

// true once the browser has actually started the context — until then nothing is audible this way
export const speakerLive = () => out && ctx?.state === "running";

export const setVolume = (v) => {
  vol = Math.max(0, Math.min(1.6, v));
  applyGain();
  return vol;
};

// TWO VOICES FROM THE SAME PROVIDER ARE NOT THE SAME LOUDNESS. Measured over three different lines
// each, same model and settings: Keith Muoki -16.2 LUFS (peaks already at full scale), Yna Agalo
// -23.4 LUFS — she arrives SEVEN DECIBELS quieter, which is why she sounded faint after him. The
// trim brings both to the same presence in the room (and the limiter above keeps her lifted peaks
// from clipping). It is a Lab setting, so whoever casts a new voice can retune it.
export const setTrim = (t) => {
  trim = Math.max(0.25, Math.min(2.5, Number(t) || 1));
  applyGain();
  return trim;
};

export const getTrim = () => trim;

export const gainValue = () => (gain ? gain.gain.value : null);

// how hard the limiter is holding the peaks down right now, in dB (0 = not working at all)
export const limiting = () => (lim ? lim.reduction : null);

export const getVolume = () => vol;

export const ctxState = () => ctx?.state || "none";

// hand the sound back to the <audio> element (iOS: earpiece, held to the ear)
export const releaseOutput = () => {
  try { gain?.disconnect(); } catch { /* gone */ }
  out = false;
};

export const setSource = (s) => { source = s; };

// dev-only probe, so the "second sitting came up silent" fault can be tested without spending a
// realtime line (same spirit as window.__griotLive in Session.jsx)
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__griotAudio = { attachPlayback, releaseOutput, speakerLive, playbackCaptured, resume, ctxState, setVolume, setTrim, getTrim, gainValue, limiting, attachRemote, setSource, read: () => read() };
}

// what the face should react to right now

const rms = (an, buf) => {
  if (!an) return 0;
  an.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
};

export const micLevel = () => (micSrc ? rms(micAn, micBuf) : 0);

export const read = () => {
  const active = source === "playback" ? playSrc : micSrc;
  if (!active) return { level: 0, treble: 0 };
  const an = source === "playback" ? playAn : micAn;
  const buf = source === "playback" ? playBuf : micBuf;
  const level = Math.min(1, rms(an, buf) * 3.2);
  an.getByteFrequencyData(freqBuf);
  const from = Math.floor(freqBuf.length * 0.45);
  let hi = 0;
  for (let i = from; i < freqBuf.length; i++) hi += freqBuf[i];
  const treble = Math.min(1, (hi / ((freqBuf.length - from) * 255)) * 4);
  return { level, treble };
};
